import {Component, HostListener, Inject, OnInit, ViewEncapsulation} from '@angular/core';
import { fuseAnimations } from '@fuse/animations';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { locale as english } from './i18n/en';
import { locale as vietnam } from './i18n/vn';
import { FuseTranslationLoaderService } from '../../../@fuse/services/translation-loader.service';
import { AccountService } from '../services';
import { UserProfile } from '../../../@aes/core/auth/auth.models';
import { Store } from '@ngrx/store';
import { changePasswordSave, changePasswordSuccess } from './state-management/change-password.actions';
import { ChangePasswordState } from './state-management/change-password.models';
import { BaseBasicDialog } from '@appshared';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FuseProgressBarService } from '../../../@fuse/components/progress-bar/progress-bar.service';
import { ConfirmPasswordErrorStateMatcher, CustomValidators } from './change-password.directive';
import { FuseSuccessDialogComponent } from '../../../@fuse/components/success-dialog/success-dialog.component';
import { Router } from '@angular/router';
import { authModuleConfig } from '../auth.config';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class ChangePasswordComponent extends BaseBasicDialog implements OnInit {
    constructor(
        public dialogRef: MatDialogRef<FuseSuccessDialogComponent>,
        protected _fuseTranslationLoaderService: FuseTranslationLoaderService,
        protected _fuseProgressBarService: FuseProgressBarService,
        private _accountService: AccountService,
        private _store: Store<any>,
        @Inject(MAT_DIALOG_DATA) public data,
        public matDialog: MatDialog,
        private _router: Router,
        private _translateService: TranslateService
    ) {
        super(dialogRef, _fuseTranslationLoaderService, _fuseProgressBarService);
        this._fuseTranslationLoaderService.loadTranslations(english, vietnam);
        this.changePasswordState = data.data;
    }
    confirmPasswordStateMatcher = new ConfirmPasswordErrorStateMatcher();
    changePasswordForm: FormGroup;
    hideOldPassword = true;
    hideNewPassword = true;
    hideConfirmPassword = true;
    changePasswordState: ChangePasswordState;
    changePasswordErrorCode: string = null;

    ngOnInit(): void {
        this.createChangePasswordForm();
    }

    onChangePasswordClick(): void{
        this.dialogRef.disableClose = true;
        this._fuseProgressBarService.show();
        const currentPassword = this.changePasswordForm.get('oldPassword').value;
        const newPassword = this.changePasswordForm.get('newPassword').value;
        const confirmPassword = this.changePasswordForm.get('confirmPassword').value;

        this._store.dispatch(changePasswordSave({
            currentPassword: currentPassword,
            newPassword: newPassword,
            confirmPassword: confirmPassword,
        }));

        if (!this.isFormDataValid()) {
            return;
        }

        this._accountService.changePassword(currentPassword, newPassword).subscribe(
            (result: UserProfile) => {
                this.changePasswordForm.reset();
                this.changePasswordErrorCode = null;
                this.dialogRef.close();
                this._fuseProgressBarService.hide();
                this._store.dispatch(changePasswordSuccess());
                const successDialog = this.matDialog.open(FuseSuccessDialogComponent, {
                    id: 'change-password-success-dialog',
                    autoFocus : false,
                    disableClose: true,
                    maxWidth: '360px',
                });
                successDialog.componentInstance.data = {
                    title : this._translateService.instant('CHANGE_PASSWORD_SUCCESS.TITLE'),
                    content : this._translateService.instant('CHANGE_PASSWORD_SUCCESS.MESSAGE'),
                    close : this._translateService.instant('CHANGE_PASSWORD_SUCCESS.CLOSE'),
                };
                successDialog.afterClosed().subscribe(() => {
                    this._router.navigate([authModuleConfig.loginPageRoute]).then();
                });
            },
            (error) => {
                if (error.error.errorCode === 'POSER-REQ-014') {
                    this.changePasswordErrorCode = error.error.errorCode;
                } else {
                    this.changePasswordErrorCode = 'OTHER';
                }
                this.dialogRef.disableClose = false;
                this._fuseProgressBarService.hide();
            }
        );
    }

    toggleHideOldPassword(): void {
        this.hideOldPassword = !this.hideOldPassword;
    }
    toggleHideNewPassword(): void {
        this.hideNewPassword = !this.hideNewPassword;
    }
    toggleHideConfirmPassword(): void {
        this.hideConfirmPassword = !this.hideConfirmPassword;
    }

    isFormDataValid(): boolean {
        const currentPassWord = this.changePasswordForm.get('oldPassword').value;
        const newPassWord = this.changePasswordForm.get('newPassword').value;
        const confirmPassWord = this.changePasswordForm.get('confirmPassword').value;
        return currentPassWord && newPassWord && confirmPassWord && newPassWord === confirmPassWord;
    }

    createChangePasswordForm(): void {
        const currentPw = this.changePasswordState.currentPassword;
        const newPw = this.changePasswordState.newPassword;
        const confirmPw = this.changePasswordState.confirmPassword;
        this.changePasswordForm = new FormGroup(
            {
                oldPassword: new FormControl(currentPw, [Validators.required]),
                newPassword: new FormControl(newPw, ),
                confirmPassword: new FormControl(confirmPw, ),
            }
        );

        this.changePasswordForm.get('newPassword').setValidators([
            Validators.required,
            Validators.minLength(6),
            CustomValidators.newPasswordNotSame(this.changePasswordForm),
            CustomValidators.newPasswordWhiteSpace()
        ]);

        this.changePasswordForm.get('confirmPassword').setValidators([
            Validators.required,
            CustomValidators.confirmPasswordMath(this.changePasswordForm)
        ]);

        if (currentPw || newPw || confirmPw) {
            this.changePasswordForm.markAsTouched();
            this.changePasswordForm.get('oldPassword').markAsTouched();
            this.changePasswordForm.get('newPassword').markAsTouched();
            this.changePasswordForm.get('confirmPassword').markAsTouched();
            this.changePasswordForm.get('oldPassword').updateValueAndValidity();
            this.changePasswordForm.get('newPassword').updateValueAndValidity();
            this.changePasswordForm.get('confirmPassword').updateValueAndValidity();
        }
    }

    confirmPassWordErrorName(): string {
        const confirmPasswordControl = this.changePasswordForm.get('confirmPassword');
        const isRequired = confirmPasswordControl.hasError('required');
        const isNotSame = confirmPasswordControl.hasError('notSame');
        if (isRequired) {
            return 'REQUIRED';
        }
        if (isNotSame) {
            return 'NOT_SAME';
        }
        return null;
    }

    newPassWordErrorName(): string {
        const newPasswordControl = this.changePasswordForm.get('newPassword');
        if (newPasswordControl.hasError('required')) {
            return 'REQUIRED';
        }
        if (newPasswordControl.hasError('minlength')) {
            return 'MINLENGTH';
        }
        if (newPasswordControl.hasError('notSame')) {
            return 'NOT_SAME';
        }
        if (newPasswordControl.hasError('whitespace')) {
            return 'WHITESPACE';
        }
        return null;
    }
}

